import { MoreMenu, Tooltip, TooltipProvider, TooltipTrigger, TooltipContent } from '@components/atoms';
import { Button, ButtonSize, ButtonVariant } from '@components/molecules';
import type { ColumnDef } from '@tanstack/react-table';
import * as React from 'react';
import { DEFAULT_ACTION_BAR_ACTION_USABILITY_MESSAGE, DEFAULT_INLINE_ACTION_COLUMN_WIDTH } from '../../data-grid.constants';
import type { DataGridActionColumnDef, DataGridActionDisabledReason } from '../../data-grid.type';
import { Menu as BaseMenu } from '../menu/menu';

const DEFAULT_USABILITY_RESULT = {
	message: DEFAULT_ACTION_BAR_ACTION_USABILITY_MESSAGE,
};
const DEFAULT_NO_ACTIONS_MESSAGE = 'No actions available for this row.';

const renderDisabledMenu = (reason: string) => (
	<div className="relative inline-block cursor-default text-left">
		<TooltipProvider delayDuration={300}>
			<Tooltip>
				<TooltipTrigger asChild>
					<div className="flex h-8 w-8 cursor-default items-center justify-center text-secondary">
						<MoreMenu />
					</div>
				</TooltipTrigger>
				<TooltipContent>
					<p>{reason}</p>
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	</div>
);

const renderDisabledMenuCell = (reason: string) => (
	<div className="relative flex h-full w-full items-center justify-end gap-1">{renderDisabledMenu(reason)}</div>
);

export function createActionColumn<T>(def: DataGridActionColumnDef<T>): ColumnDef<T> | null {
	// If no actions are provided, don't render the column
	if (def.actions.length === 0) {
		return null;
	}

	const inlineWidth = def.width ?? DEFAULT_INLINE_ACTION_COLUMN_WIDTH;
	const hasInlineButtons = Boolean(def.inline) || def.actions.some(action => action.inline);
	const hasMenuActionsConfigured = def.actions.some(action => !(Boolean(def.inline) || Boolean(action.inline)));

	return {
		id: def.key,
		// header: def.header || '', // NOTE: If for some reason the header needs a title
		header: '',
		...(hasInlineButtons ? { size: inlineWidth, meta: { hasExplicitWidth: true } } : {}),
		cell: ({ row }) => {
			// Check if all actions should be disabled via actionsUsability or hidden
			if (def.actionsUsability) {
				const usabilityResult = def.actionsUsability(row.original);

				if (usabilityResult !== true) {
					const disabledReason =
						(usabilityResult as DataGridActionDisabledReason)?.message || DEFAULT_USABILITY_RESULT.message;

					return renderDisabledMenuCell(disabledReason);
				}
			}

			const options = def.actions
				.filter(({ hidden }) => (typeof hidden === 'function' ? !hidden(row.original) : !hidden))
				.map(action => {
					const label = typeof action.label === 'function' ? action.label(row.original) : action.label;

					// Check if action is usable for this row
					let disabled = false;
					let disabledReason: string | undefined;

					if (action.isUsable) {
						const usabilityResult = action.isUsable(row.original);

						if (usabilityResult !== true) {
							disabled = true;
							disabledReason =
								(usabilityResult as DataGridActionDisabledReason)?.message || DEFAULT_USABILITY_RESULT.message;
						}
					}

				return {
					disabled,
					disabledReason,
					label,
					icon: action.icon,
					onClick: () => action.onClick(row.original),
					dataTestId: action.dataTestId,
					inline: Boolean(def.inline) || Boolean(action.inline),
				};
				});

			if (options.length === 0) {
				return hasMenuActionsConfigured ? renderDisabledMenuCell(DEFAULT_NO_ACTIONS_MESSAGE) : null;
			}

			const buttonOptions = options.filter(option => option.inline);
			const menuOptions = options.filter(option => !option.inline);

			return (
				<div className="relative flex h-full w-full items-center justify-end gap-1">
					{buttonOptions.length > 0 && (
						<TooltipProvider delayDuration={300}>
							{buttonOptions.map((option, index) => {
								const Icon = option.icon;
								const button = (
									<Button
										variant={ButtonVariant.HYPERLINK}
										size={ButtonSize.SM}
										className="hover:text-primary"
										disabled={option.disabled}
										data-testid={option.dataTestId}
										onClick={event => {
											event.stopPropagation();
											option.onClick();
										}}
									>
										{Icon ? (
											<React.Fragment>
												<Icon className="h-4 w-4" />
												<span className="sr-only">{option.label}</span>
											</React.Fragment>
										) : (
											option.label
										)}
									</Button>
								);

								return (
									<Tooltip key={index}>
										<TooltipTrigger asChild>
											{option.disabled ? <div className="inline-flex">{button}</div> : button}
										</TooltipTrigger>
										<TooltipContent>
											<p>{option.disabledReason ?? option.label}</p>
										</TooltipContent>
									</Tooltip>
								);
							})}
						</TooltipProvider>
					)}
					{menuOptions.length > 0 ? (
						<BaseMenu options={menuOptions} />
					) : hasMenuActionsConfigured ? (
						renderDisabledMenu(DEFAULT_NO_ACTIONS_MESSAGE)
					) : null}
				</div>
			);
		},
		enableResizing: false,
	};
}
